import React from 'react';
import { Button } from './ui/button';
import SearchBar from './SearchBar';
import DroppablePriorityButton from './DroppablePriorityButton';
import { Plus, LogOut, CheckSquare, Settings as SettingsIcon, Calendar, ChevronLeft, ChevronRight, Printer } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';

const DashboardHeader = ({
  theme,
  currentTheme,
  username,
  viewMode,
  setViewMode,
  selectedDate,
  setSelectedDate,
  onAddTask,
  onOpenSettings,
  onPrint,
  onLogout,
  searchQuery,
  setSearchQuery,
  clearSearch,
  isSearching,
  searchResults,
  layoutMode,
  selectedPriorityCategory,
  setSelectedPriorityCategory,
  categoryInfo,
}) => {
  const themeButtonClass = (themeKey) => {
    const map = {
      forest: 'border-[#b8c4b0] text-[#e0e8dc] hover:bg-[#3d6b4a]',
      ocean: 'border-[#5a8aaa] text-[#e0eef5] hover:bg-[#143e5e]',
      floral: 'border-[#c8a0b0] text-[#f8eff2] hover:bg-[#b04868]',
      executive: 'border-[#8FA8C8] text-[#C9D6E8] hover:bg-[#1A3A5C]',
      sunset: 'border-[#d4a888] text-[#fae8d8] hover:bg-[#a83020]',
    };
    return map[themeKey] || '';
  };

  const accentBg = theme === 'forest' ? 'bg-[#4a7c59]' : theme === 'executive' ? 'bg-[#DAA520]' : theme === 'ocean' ? 'bg-[#1a4a6e]' : theme === 'floral' ? 'bg-[#c45a78]' : theme === 'sunset' ? 'bg-[#c0392b]' : 'bg-blue-600 dark:bg-blue-500';

  const addBtnClass = theme === 'forest' ? 'bg-[#4a7c59] hover:bg-[#3d6b4a]' : theme === 'executive' ? 'bg-[#DAA520] hover:bg-[#c49520]' : theme === 'ocean' ? 'bg-[#1a4a6e] hover:bg-[#143e5e]' : theme === 'floral' ? 'bg-[#c45a78] hover:bg-[#b04868]' : theme === 'sunset' ? 'bg-[#c0392b] hover:bg-[#a83020]' : 'bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600';

  return (
    <header className={`${currentTheme.colors.headerBg} border-b ${currentTheme.colors.headerBorder} sticky top-0 z-40 shadow-sm`}>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-2 sm:py-4">
        {/* Line 1: App name + action buttons */}
        <div className="flex items-center justify-between gap-1 sm:gap-3">
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <div className={`${accentBg} p-1 sm:p-2 rounded-lg flex-shrink-0`}>
              <CheckSquare className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className={`text-sm sm:text-2xl font-bold ${currentTheme.colors.primaryText} ${currentTheme.headingFont || ''} truncate`}>
                {theme === 'executive' ? 'TASKPRIORITY PRIME' : 'TaskPriority Prime'}
              </h1>
              <p className={`text-xs sm:text-sm ${currentTheme.colors.secondaryText} hidden sm:block`}>Welcome, {username}</p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            <select
              value={viewMode}
              onChange={(e) => {
                setViewMode(e.target.value);
                if (e.target.value === 'today') setSelectedDate(new Date());
              }}
              className="h-7 sm:h-10 px-1 sm:px-3 bg-white border border-slate-300 rounded-md text-slate-900 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              data-testid="view-mode-select"
            >
              <option value="today">Today</option>
              <option value="next7days">7 Days</option>
              <option value="allFuture">Future</option>
            </select>

            {viewMode === 'today' && (
              <div className="hidden sm:flex items-center gap-2 sm:gap-3 bg-blue-50 dark:bg-blue-900 px-3 sm:px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-700">
                <Button onClick={() => setSelectedDate(subDays(selectedDate, 1))} variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-blue-100 dark:hover:bg-blue-800" data-testid="prev-day-btn">
                  <ChevronLeft className="w-4 h-4 text-blue-900 dark:text-blue-100" />
                </Button>
                <div className="flex items-center gap-2 min-w-[140px] sm:min-w-[180px] justify-center">
                  <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                  <input
                    type="date"
                    value={format(selectedDate, 'yyyy-MM-dd')}
                    onChange={(e) => setSelectedDate(new Date(e.target.value + 'T00:00:00'))}
                    className="font-medium text-blue-900 dark:text-blue-100 text-sm sm:text-base bg-transparent border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
                    style={{ colorScheme: 'light' }}
                    data-testid="date-picker"
                  />
                </div>
                <Button onClick={() => setSelectedDate(addDays(selectedDate, 1))} variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-blue-100 dark:hover:bg-blue-800" data-testid="next-day-btn">
                  <ChevronRight className="w-4 h-4 text-blue-900 dark:text-blue-100" />
                </Button>
                <Button onClick={() => setSelectedDate(new Date())} variant="outline" size="sm" className="h-7 sm:h-8 text-xs border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-800" data-testid="today-btn">
                  Today
                </Button>
              </div>
            )}

            <Button onClick={onAddTask} className={`${addBtnClass} h-7 w-7 sm:h-10 sm:w-auto text-sm sm:text-base px-0 sm:px-4 text-white`} size="icon" data-testid="add-task-btn" title="Add Task (N)">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Task</span>
              <kbd className="hidden sm:inline ml-2 text-[10px] font-mono opacity-60 bg-white/20 px-1.5 py-0.5 rounded">N</kbd>
            </Button>
            <Button onClick={onOpenSettings} variant="outline" size="icon" className={`h-7 w-7 sm:h-10 sm:w-10 ${themeButtonClass(theme)}`} title="Settings" data-testid="settings-btn">
              <SettingsIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
            <Button onClick={onPrint} variant="outline" size="icon" className={`h-7 w-7 sm:h-10 sm:w-10 print-hide ${themeButtonClass(theme)}`} title="Print Today's List" data-testid="print-btn">
              <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
            <Button onClick={onLogout} variant="outline" size="icon" className={`h-7 w-7 sm:h-10 sm:w-10 ${themeButtonClass(theme)}`} title="Logout" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
          </div>
        </div>

        {/* Mobile-only: calendar row */}
        {viewMode === 'today' && (
          <div className="flex sm:hidden items-center justify-center gap-2 mt-3 bg-blue-50 dark:bg-blue-900 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-700">
            <Button onClick={() => setSelectedDate(subDays(selectedDate, 1))} variant="ghost" size="icon" className="h-7 w-7 hover:bg-blue-100 dark:hover:bg-blue-800" data-testid="prev-day-btn-mobile">
              <ChevronLeft className="w-4 h-4 text-blue-900 dark:text-blue-100" />
            </Button>
            <div className="flex items-center gap-2 min-w-[140px] justify-center">
              <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-300" />
              <input
                type="date"
                value={format(selectedDate, 'yyyy-MM-dd')}
                onChange={(e) => setSelectedDate(new Date(e.target.value + 'T00:00:00'))}
                className="font-medium text-blue-900 dark:text-blue-100 text-sm bg-transparent border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
                style={{ colorScheme: 'light' }}
              />
            </div>
            <Button onClick={() => setSelectedDate(addDays(selectedDate, 1))} variant="ghost" size="icon" className="h-7 w-7 hover:bg-blue-100 dark:hover:bg-blue-800" data-testid="next-day-btn-mobile">
              <ChevronRight className="w-4 h-4 text-blue-900 dark:text-blue-100" />
            </Button>
            <Button onClick={() => setSelectedDate(new Date())} variant="outline" size="sm" className="h-7 text-xs border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-800">
              Today
            </Button>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center justify-center gap-2 sm:gap-3 mt-2 sm:mt-3">
          <div className="w-full sm:max-w-md min-w-0">
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              clearSearch={clearSearch}
              isSearching={isSearching}
              searchResults={searchResults}
              setSelectedDate={setSelectedDate}
            />
          </div>
        </div>

        {/* Priority Stacked: Category selector buttons */}
        {layoutMode === 'priority-stacked' && (
          <div className="flex justify-center gap-3 sm:gap-4 mt-2 sm:mt-3 pb-1">
            {['A', 'B', 'C'].map(cat => (
              <DroppablePriorityButton
                key={cat}
                id={`priority-btn-${cat}`}
                category={cat}
                isSelected={selectedPriorityCategory === cat}
                info={categoryInfo[cat]}
                onClick={() => setSelectedPriorityCategory(cat)}
              />
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default DashboardHeader;
